import { CircleX, Eye, GitCompareArrows, Link2 } from 'lucide-react';
import type { ReactElement } from 'react';
import { IconButtonWithTooltip } from '@ooh/ui/components/icon-button-with-tooltip';
import type { OfferVersionStatus } from '@ooh/contracts';

export type WithdrawAcceptanceMode = 'withdraw' | 'registerRejection';

export interface VersionRowAction {
  label: string;
  onClick?: () => void;
  testId?: string;
  link?: ReactElement;
  wrap?: (trigger: ReactElement) => ReactElement;
}

interface VersionRowActionsProps {
  actionsLabel: string;
  preview: VersionRowAction;
  clientShare?: VersionRowAction;
  compare?: VersionRowAction;
  withdraw?: VersionRowAction;
}

function renderAction(action: VersionRowAction, trigger: ReactElement): ReactElement {
  return action.wrap ? action.wrap(trigger) : trigger;
}

export function VersionRowActions({
  actionsLabel,
  preview,
  clientShare,
  compare,
  withdraw,
}: VersionRowActionsProps) {
  return (
    <div
      className="mt-4 flex flex-wrap items-center gap-2 border-t border-border/60 pt-4"
      aria-label={actionsLabel}
      data-testid="version-row-actions"
      onClick={(event) => event.stopPropagation()}
    >
      <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {actionsLabel}
      </span>
      {preview.link
        ? renderAction(
            preview,
            <IconButtonWithTooltip
              asChild
              tooltip={preview.label}
              size="icon-md"
              testId={preview.testId}
            >
              {preview.link}
            </IconButtonWithTooltip>,
          )
        : renderAction(
            preview,
            <IconButtonWithTooltip
              icon={Eye}
              tooltip={preview.label}
              size="icon-md"
              onClick={preview.onClick}
              testId={preview.testId}
            />,
          )}
      {clientShare
        ? renderAction(
            clientShare,
            <IconButtonWithTooltip
              icon={Link2}
              tooltip={clientShare.label}
              size="icon-md"
              onClick={clientShare.onClick}
              testId={clientShare.testId}
            />,
          )
        : null}
      {compare
        ? renderAction(
            compare,
            <IconButtonWithTooltip
              icon={GitCompareArrows}
              tooltip={compare.label}
              variant="ghost"
              size="icon-md"
              onClick={compare.onClick}
              testId={compare.testId}
            />,
          )
        : null}
      {withdraw
        ? renderAction(
            withdraw,
            <IconButtonWithTooltip
              icon={CircleX}
              tooltip={withdraw.label}
              variant="destructive"
              size="icon-md"
              onClick={withdraw.onClick}
              testId={withdraw.testId}
            />,
          )
        : null}
    </div>
  );
}

interface GetVersionWithdrawActionParams {
  versionId: number;
  status: OfferVersionStatus;
  canEdit: boolean;
  withdrawLabel: string;
  registerRejectionLabel: string;
  onWithdrawClick: (versionId: number, mode: WithdrawAcceptanceMode) => void;
}

export function getVersionWithdrawAction({
  versionId,
  status,
  canEdit,
  withdrawLabel,
  registerRejectionLabel,
  onWithdrawClick,
}: GetVersionWithdrawActionParams): VersionRowAction | undefined {
  if (!canEdit) return undefined;

  if (status === 'ACCEPTED') {
    return {
      label: withdrawLabel,
      testId: 'withdraw-acceptance-button',
      onClick: () => onWithdrawClick(versionId, 'withdraw'),
    };
  }

  if (status === 'PUBLISHED') {
    return {
      label: registerRejectionLabel,
      testId: 'register-rejection-button',
      onClick: () => onWithdrawClick(versionId, 'registerRejection'),
    };
  }

  return undefined;
}
